import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuAction,
} from "~/react/ui/dropdown-menu"
import { cn } from "~/lib/utils"

interface BulkAction {
  label: string
  icon: string
  onClick: () => void
  disabled?: boolean
}

interface DataTableBulkActionsBarProps {
  totalCount: number
  pageCount: number
  selectedCount: number
  onClearSelection: () => void
  onSelectAll: () => void
  onSelectPage: () => void
  actions?: BulkAction[]
}

export function DataTableBulkActionsBar({
  totalCount,
  pageCount,
  selectedCount,
  onClearSelection,
  onSelectAll,
  onSelectPage,
  actions = [],
}: DataTableBulkActionsBarProps) {
  const visibleActions = actions.length <= 3 ? actions : actions.slice(0, 3)
  const collapsedActions = actions.length > 3 ? actions.slice(3) : []
  return (
    <header
      className={cn("h-table-bulk-actions", selectedCount > 0 && "is-visible")}
    >
      <Button
        size="sm"
        iconOnly
        type="button"
        onClick={onClearSelection}
        title={i18n.t("js.data_table.bulk_actions.clear_selection")}
        aria-label={i18n.t("js.data_table.bulk_actions.clear_selection")}
      >
        <Icon icon="times" aria-hidden="true" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" type="button">
            <div>
              {i18n.t("js.data_table.bulk_actions.count_selected", {
                count: selectedCount,
              })}
            </div>
            <Icon icon="angle-down" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem>
            <DropdownMenuAction type="button" onClick={onSelectAll}>
              {i18n.t("js.data_table.bulk_actions.select_all")}
              <small className="grey ml-auto">
                ({totalCount.toLocaleString("en-US")})
              </small>
            </DropdownMenuAction>
          </DropdownMenuItem>
          <DropdownMenuItem>
            <DropdownMenuAction type="button" onClick={onSelectPage}>
              {i18n.t("js.data_table.bulk_actions.select_page")}
              <small className="grey ml-auto">
                ({pageCount.toLocaleString("en-US")})
              </small>
            </DropdownMenuAction>
          </DropdownMenuItem>
          <DropdownMenuItem className="h-table-bulk-actions__unselect">
            <DropdownMenuAction type="button" onClick={onClearSelection}>
              {i18n.t("js.data_table.bulk_actions.unselect_all")}
            </DropdownMenuAction>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {visibleActions.length > 0 && (
        <div className="h-table-bulk-actions__actions">
          {visibleActions.map((action, index) => (
            <Button
              key={index}
              size="sm"
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
            >
              <Icon
                icon={action.icon.replace("fa-", "")}
                size="sm"
                variant="regular"
                aria-hidden="true"
              />
              {action.label}
            </Button>
          ))}
        </div>
      )}
      {collapsedActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              iconOnly
              type="button"
              aria-label={i18n.t(
                "js.email_accounts_center.actions.more_actions_aria",
              )}
            >
              <Icon
                icon="ellipsis-h"
                size="sm"
                variant="regular"
                aria-hidden="true"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {collapsedActions.map((action, index) => (
              <DropdownMenuItem key={index}>
                <DropdownMenuAction
                  type="button"
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  <Icon
                    icon={action.icon.replace("fa-", "")}
                    size="sm"
                    variant="regular"
                    aria-hidden="true"
                  />
                  {action.label}
                </DropdownMenuAction>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  )
}
